import { Input } from "@strivelabs/ui-core"
import { IconButton } from "@strivelabs/ui-core/v2"
import { Eye, EyeOff } from "lucide-react"
import { forwardRef, useState, type InputHTMLAttributes } from "react"

type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type">

/**
 * Single-line password field with a built-in show/hide toggle. Forwards refs
 * so it slots cleanly into react-hook-form's `register(...)`. Wherever you'd
 * write a `<Input type="password" />` plus an `Eye/EyeOff` button, use this
 * instead.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
    function PasswordInput ({ className, ...props }, ref) {
        const [ show, setShow ] = useState(false)
        const inputClassName = [ "pr-10", className ].filter(Boolean).join(" ")

        return (
            <div className="relative">
                <Input
                    ref={ref}
                    type={show ? "text" : "password"}
                    className={inputClassName}
                    {...props}
                />
                <IconButton
                    size="sm"
                    tabIndex={-1}
                    aria-label={show ? "Hide password" : "Show password"}
                    onClick={() => setShow((v) => { return !v })}
                    className="absolute inset-y-0 right-1 my-auto"
                >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </IconButton>
            </div>
        )
    }
)
